import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';
import { Shield, Mail, Lock, Smartphone, Loader2, AlertCircle } from 'lucide-react';

const SuperAdminLogin = () => {
  const navigate = useNavigate();
  const { login, sessionExpired, sessionExpiredMessage } = useSuperAdminAuth();

  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    mfaMethod: 'email'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const identifier = formData.mfaMethod === 'sms' ? formData.phone : formData.email;
      // Password is required for BOTH channels — the OTP method only selects where the code is delivered.
      const result = await login(
        identifier,
        formData.password,
        formData.mfaMethod
      );

      if (result.otp_sent) {
        localStorage.setItem('superadmin_temp_token', result.temp_token);
        localStorage.setItem('superadmin_otp_method', result.otp_method);
        localStorage.setItem(
          'superadmin_masked_contact',
          result.masked_contact || ''
        );

        navigate('/superadmin-portal/verify-otp', {
          state: {
            tempToken: result.temp_token,
            otpMethod: result.otp_method,
            maskedContact: result.masked_contact
          }
        });
      }
    } catch (err) {
      // Friendly, non-enumerating auth errors — never surface raw Axios text or backend internals.
      const status = err.response?.status;
      let msg;
      if (status === 401 || status === 403) {
        msg = 'Invalid email or password.';            // same message for bad password / no access — no enumeration
      } else if (status === 429) {
        msg = 'Too many attempts. Please wait a moment and try again.';
      } else if (status === 400) {
        msg = 'Please enter a valid email and password.';
      } else if (status >= 500) {
        msg = 'The service is temporarily unavailable. Please try again shortly.';
      } else if (!err.response) {
        msg = 'Network error. Please check your connection and try again.';
      } else {
        msg = 'Authentication failed. Please check your credentials and try again.';
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <Shield className="h-8 w-8 text-purple-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Super Admin Portal
          </h1>
          <p className="text-gray-600">
            Secure access for system administrators
          </p>
        </div>

        {/* Graceful re-auth: an expired session lands here with a clear, non-alarming prompt (not an error). */}
        {sessionExpired && !error && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" role="status">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
            <p className="text-sm text-amber-800">{sessionExpiredMessage}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* MFA method selector — placed first so the identifier field below adapts */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Two-Factor Authentication Method
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, mfaMethod: 'email' })}
                className={`p-4 border-2 rounded-lg text-left ${
                  formData.mfaMethod === 'email'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200'
                }`}
              >
                <Mail className="h-6 w-6 mb-1 text-purple-600" />
                <div className="font-medium text-sm">Email OTP</div>
                <div className="text-xs text-gray-500 mt-0.5">Code sent to your registered email address</div>
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, mfaMethod: 'sms' })}
                className={`p-4 border-2 rounded-lg text-left ${
                  formData.mfaMethod === 'sms'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200'
                }`}
              >
                <Smartphone className="h-6 w-6 mb-1 text-purple-600" />
                <div className="font-medium text-sm">SMS OTP</div>
                <div className="text-xs text-gray-500 mt-0.5">Code sent to your registered phone number</div>
              </button>
            </div>
          </div>

          {/* Identifier — Email or Phone depending on MFA method */}
          {formData.mfaMethod === 'sms' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <div className="relative">
                <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="tel"
                  required
                  placeholder="+1 (780) 000-0000"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  disabled={loading}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                📱 Verification code will be sent via SMS to this number.
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  disabled={loading}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                📧 Verification code will be sent to your admin email after password check.
              </p>
            </div>
          )}

          {/* Password — required for BOTH channels (the OTP method only chooses code delivery) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                disabled={loading}
              />
            </div>
          </div>


          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-600 text-white py-3 rounded-lg"
          >
            {loading ? <><Loader2 className="inline h-5 w-5 animate-spin mr-2" />Sending...</> : formData.mfaMethod === 'sms' ? 'Send OTP' : 'Continue to Verification'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SuperAdminLogin;